import { RadioGroup, RadioGroupIndicator, RadioGroupItem } from '@radix-ui/react-radio-group';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Announcement03, Target02 } from '@untitled-ui/icons-react';
import type { TFunctionNonStrict } from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { Button } from '../../../components/buttons/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../components/dialog.tsx';
import { FeaturedIcon } from '../../../components/featured-icon.tsx';
import { TextField } from '../../../components/inputs/text-field';
import { BlymLoading } from '../../../components/loading';
import { SelectField } from '../../../components/select-field';
import { Slider, type SliderItem } from '../../../components/slider';
import { getConfig } from '../../../config';
import { AiStatus } from '../../../entity/api/ai';
import type { JobStatusRes } from '../../../entity/api/routes/brand';
import { urlSchema } from '../../../entity/api/shared';
import { trackError } from '../../../error/tracking.ts';
import { captureException } from '../../../logger/capture-exception.ts';
import {
  generateBrandContextFromOnboarding,
  getBrandJobStatus,
  pollBrandJobStatus,
} from '../../../sdk/brand.ts';
import { getQueryClient } from '../../../sdk/request';
import { updateUser } from '../../../sdk/user';
import { PostHogEvents, capturePostHogEvent } from '../../../services/posthog';
import { fetchBrandByIdImperative } from '../../../services/storage/brands-queries';
import { useUpdateOrganization } from '../../../services/storage/organizations-queries';
import { getAuthUserFromCache } from '../../../services/storage/users-queries';
import { tGlobal, tNamespace } from '../../../services/translation';
import { getSelectedBrand } from '../../../shared/brand.ts';
import { countriesOptions, getLanguages } from '../../../shared/countries';
import { Feature, isFeatureAvailable } from '../../../shared/feature';
import { usePostHogSessionRecording } from '../../../shared/hooks/use-posthog-session-recording';
import { getSelectedOrganization } from '../../../shared/organization.ts';
import { parseFirstZodError } from '../../../shared/validation';
import { OnboardingLayout } from './-onboarding-layout.tsx';

type OnboardingOrigin =
  | 'google_search'
  | 'linkedin'
  | 'chatgpt_ai'
  | 'events'
  | 'word_of_mouth'
  | 'meta'
  | 'youtube'
  | 'other';

const t = tNamespace('app.onboarding');

export const Route = createFileRoute('/app/onboarding/')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
  beforeLoad: function () {
    const isOnboardingAvailable = isFeatureAvailable(Feature.ONBOARDING);
    if (!isOnboardingAvailable) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/app' });
    }
    // Get user from TanStack Query cache (populated during auth initialization)
    const queryClient = getQueryClient();
    const currentUser = getAuthUserFromCache(queryClient);
    const currentOrganization = getSelectedOrganization(currentUser);
    const currentBrand = getSelectedBrand(currentUser);

    if (
      currentOrganization != null &&
      currentOrganization.hasCompletedOnboarding === true &&
      currentBrand != null
    ) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/app' });
    }

    return { organizationId: currentOrganization?._id, brandId: currentBrand?._id };
  },
});

const GENERATION_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

const ORIGIN_OPTIONS: Array<{ value: OnboardingOrigin; labelKey: string }> = [
  { value: 'youtube', labelKey: 'originYoutube' },
  { value: 'google_search', labelKey: 'originGoogleSearch' },
  { value: 'linkedin', labelKey: 'originLinkedin' },
  { value: 'chatgpt_ai', labelKey: 'originChatgptAi' },
  { value: 'events', labelKey: 'originEvents' },
  { value: 'word_of_mouth', labelKey: 'originWordOfMouth' },
  { value: 'meta', labelKey: 'originFacebook' },
  { value: 'other', labelKey: 'originOther' },
];

type OnboardingFormData = {
  urls: {
    homepage: string;
    blogs: string[];
  };
  country: string;
  language: string;
};

type StepConfig = {
  title: string;
  description?: string;
  icon: React.ReactNode;
  leftContent: React.ReactNode;
  showSkip: boolean;
  nextButtonText: string;
  content: React.ReactNode;
};

type LoadingConfig = {
  title: string;
  description: string | React.ReactNode;
  card: SliderItem;
};

function RouteComponent() {
  const { organizationId, brandId } = Route.useRouteContext();
  const { defaultCountry } = getConfig();
  const maxStepsOnboarding = 1;

  usePostHogSessionRecording();
  const maxStepLoading = 3;
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingStep, setLoadingStep] = useState(-1);
  const [skipButtonLoading, setSkipButtonLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalGenerationError, setModalGenerationError] = useState(false);
  const [origin, setOrigin] = useState<OnboardingOrigin>('youtube');
  const [originOther, setOriginOther] = useState('');
  const navigate = useNavigate();
  const cancelPollingRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TanStack Query mutation for updating organization
  // Destructure mutateAsync which is referentially stable (avoids useEffect re-runs)
  const { mutateAsync: updateOrganization } = useUpdateOrganization();

  // Function to start polling for job status
  const startPolling = useCallback(
    function () {
      if (brandId == null) {
        return;
      }

      setIsLoading(true);

      // Timeout
      timeoutRef.current = setTimeout(function () {
        if (cancelPollingRef.current != null) {
          cancelPollingRef.current();
        }
        setIsLoading(false);
        setModalGenerationError(true);
      }, GENERATION_TIMEOUT_MS);

      // Start polling for job status every 15 seconds
      cancelPollingRef.current = pollBrandJobStatus(
        brandId,
        function (apiResponse: JobStatusRes): void {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const currentStatus = apiResponse.status as AiStatus;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const jobError = apiResponse.error;
          const isSuccess = currentStatus === AiStatus.Success;
          const isError = currentStatus === AiStatus.Error;

          // Manage loading step progression
          setLoadingStep(function (prevStep) {
            if (prevStep < maxStepLoading) {
              return prevStep + 1;
            }
            return prevStep;
          });

          if (isSuccess) {
            // Job completed successfully we clear timeout and update organization and brand
            if (timeoutRef.current != null) {
              clearTimeout(timeoutRef.current);
            }

            if (organizationId != null) {
              const queryClient = getQueryClient();
              updateOrganization({
                id: organizationId,
                data: { hasCompletedOnboarding: true },
              })
                .then(function () {
                  return fetchBrandByIdImperative(queryClient, brandId).then(function () {
                    return brandId != null
                      ? navigate({ to: '/app/seo-strategy/$brandId', params: { brandId } })
                      : navigate({ to: '/app' });
                  });
                })
                .catch(function (err) {
                  captureException(
                    err,
                    {
                      file: 'onboarding.tsx',
                      method: 'startPolling-pollCallback',
                      infoMessage: 'unable to update organization after job completion',
                    },
                    trackError
                  );
                  setIsLoading(false);
                });
            }
          } else if (isError) {
            // Job failed - clear timeout
            if (timeoutRef.current != null) {
              clearTimeout(timeoutRef.current);
            }
            setIsLoading(false);
            setModalGenerationError(true);
            const errorMessage = jobError ?? 'Brand context generation failed';
            captureException(
              new Error(errorMessage),
              {
                file: 'onboarding.tsx',
                method: 'startPolling-pollCallback',
                infoMessage: 'brand context generation job failed',
              },
              trackError
            );
          }
          // For pending status, continue polling (nothing to do here)
        },
        15000 // Poll every 15 seconds
      );
    },
    [brandId, organizationId, navigate, maxStepLoading, updateOrganization]
  );

  // Check if there's an ongoing job on page load
  useEffect(
    function () {
      if (brandId == null) {
        return;
      }

      getBrandJobStatus(brandId)
        .then(function (apiResponse: JobStatusRes) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const currentStatus = apiResponse.status as AiStatus;
          const isPending = currentStatus === AiStatus.Pending;

          if (isPending) {
            // There's an ongoing job, start polling
            startPolling();
          }
        })
        .catch(function (err) {
          // No ongoing job or API error, we are silently ignoring it
          captureException(
            err,
            {
              file: 'onboarding.tsx',
              method: 'useEffect-checkJobStatus',
              infoMessage: 'unable to check job status on page load',
            },
            trackError
          );
        });
    },
    [brandId, startPolling]
  );

  useEffect(function () {
    return function () {
      if (cancelPollingRef.current != null) {
        cancelPollingRef.current();
      }
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const form = useForm<OnboardingFormData>({
    defaultValues: {
      urls: {
        homepage: '',
        blogs: [],
      },
      country: defaultCountry,
      language: 'en',
    },
    onSubmit: async function () {
      if (brandId == null) {
        return;
      }

      try {
        // Start the brand context generation
        await generateBrandContextFromOnboarding({
          contentId: brandId,
          country: form.state.values.country,
          language: form.state.values.language,
          homepage: form.state.values.urls.homepage,
          blogPostList: form.state.values.urls.blogs,
          productPageList: [],
        });

        // Start polling for job status
        startPolling();
      } catch (err) {
        setIsLoading(false);
        captureException(
          err,
          {
            file: 'onboarding.tsx',
            method: 'onSubmit',
            infoMessage: 'unable to submit brand context generation',
          },
          trackError
        );
      }
    },
  });

  const handleNext = useCallback(
    async function () {
      if (currentStep < maxStepsOnboarding) {
        if (currentStep === 0) {
          // Validate "other" field if selected
          if (origin === 'other' && originOther.trim() === '') {
            return;
          }

          // Save origin to user in database
          try {
            await updateUser({
              onboardingOrigin: origin,
              onboardingOriginOther: origin === 'other' ? originOther : undefined,
            });
            const originValue = origin === 'other' ? originOther : origin;
            capturePostHogEvent(PostHogEvents.ONBOARDING_ORIGIN, {
              origin: originValue,
            });
          } catch (err) {
            captureException(
              err,
              {
                file: 'onboarding.tsx',
                method: 'handleNext',
                infoMessage: 'unable to save onboarding origin',
              },
              trackError
            );
            // Continue even if saving fails - don't block the user
          }

          setCurrentStep(currentStep + 1);
          return;
        }
        return;
      }
      return form
        .handleSubmit()
        .then(function () {
          const originValue = origin === 'other' ? originOther : origin;
          capturePostHogEvent(PostHogEvents.ONBOARDING_COMPLETED, {
            origin: originValue,
            // eslint-disable-next-line camelcase
            homepage_url: form.state.values.urls.homepage,
            // eslint-disable-next-line camelcase
            blogs_urls: form.state.values.urls.blogs,
            language: form.state.values.language,
            country: form.state.values.country,
          });
        })
        .catch(function (err) {
          captureException(
            err,
            {
              file: 'onboarding.tsx',
              method: 'handleNext',
              infoMessage: 'unable to submit form',
            },
            trackError
          );
        });
    },
    [currentStep, maxStepsOnboarding, form, origin, originOther]
  );

  const handleBack = useCallback(
    function () {
      if (currentStep >= 1) {
        setCurrentStep(currentStep - 1);
      }
    },
    [currentStep]
  );

  const handleSkip = useCallback(
    function () {
      if (organizationId == null) {
        return;
      }
      setSkipButtonLoading(true);
      updateOrganization({
        id: organizationId,
        data: { hasCompletedOnboarding: true },
      })
        .then(function () {
          return brandId != null
            ? navigate({ to: '/app/seo-strategy/$brandId', params: { brandId } })
            : navigate({ to: '/app' });
        })
        .catch(function (err) {
          captureException(
            err,
            {
              file: 'onboarding.tsx',
              method: 'handleSkip',
              infoMessage: 'unable to update organization',
            },
            trackError
          );
        })
        .finally(function () {
          setSkipButtonLoading(false);
        });
    },
    [organizationId, navigate, updateOrganization]
  );

  function getFormContent(step: number): React.ReactNode {
    // Step 0: Origin selection
    if (step === 0) {
      return (
        <div className="mt-8 flex w-full flex-col gap-4">
          <RadioGroup
            className="grid grid-cols-2 gap-3"
            value={origin}
            onValueChange={function (value) {
              setOrigin(value as OnboardingOrigin);
            }}
          >
            {ORIGIN_OPTIONS.map(function (option) {
              const isSelected = origin === option.value;
              const isOther = option.value === 'other';
              return (
                <label
                  key={option.value}
                  className={twMerge(
                    'flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-all',
                    isSelected
                      ? 'border-branding-accent bg-branding-accent-50'
                      : 'border-branding-gray-200 hover:border-branding-gray-300',
                    isOther && isSelected ? 'col-span-2 py-2.5' : ''
                  )}
                >
                  <RadioGroupItem
                    value={option.value}
                    className="group size-5 shrink-0 rounded-full border border-branding-gray-300 bg-white shadow-1 hover:border-branding-accent hover:bg-branding-accent-50 focus:border-branding-accent-200 focus:outline-none focus:ring-4 focus:ring-branding-accent-200 data-[state=checked]:border-branding-accent data-[state=checked]:bg-branding-accent-50"
                  >
                    <RadioGroupIndicator className="relative flex size-full items-center justify-center after:block after:size-2.5 after:rounded-full after:bg-branding-accent" />
                  </RadioGroupItem>
                  {isOther && isSelected ? (
                    <div className="flex flex-1 items-center gap-2">
                      <span className="font-body-2-m text-branding-black whitespace-nowrap">
                        {t(option.labelKey)}
                      </span>
                      <TextField
                        sizeField="xs"
                        placeholder={t('originOtherPlaceholder')}
                        value={originOther}
                        onChange={function (e) {
                          setOriginOther(e.target.value);
                        }}
                        onClick={function (e) {
                          e.stopPropagation();
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <span className="font-body-2-m text-branding-black">{t(option.labelKey)}</span>
                  )}
                </label>
              );
            })}
          </RadioGroup>
        </div>
      );
    }

    // Step 1: Brand setup (homepage + country/language)
    if (step === 1) {
      return (
        <div className="mt-8 flex w-full flex-col gap-6">
          <form.Field
            name="urls.homepage"
            validators={{
              onChange: function (data) {
                return parseFirstZodError(urlSchema as z.ZodSchema<string>, data.value);
              },
              onSubmit: function (data) {
                return parseFirstZodError(urlSchema as z.ZodSchema<string>, data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  autoFocus
                  name={field.name}
                  sizeField="md"
                  label={t('homepage')}
                  placeholder="https://example.com"
                  value={field.state.value}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                  error={field.state.meta?.errors}
                  autoComplete="off"
                  className="w-full"
                />
              );
            }}
          />

          <div className="flex gap-4">
            <form.Field
              name="country"
              validators={{
                onChange: function (data) {
                  return parseFirstZodError(z.string().min(1, 'COUNTRY_REQUIRED'), data.value);
                },
              }}
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <SelectField
                    value={field.state.value}
                    placeholder={tGlobal('selectACountry')}
                    onChange={field.handleChange}
                    options={countriesOptions}
                    size="md"
                    contentFit={true}
                    className="w-full"
                    triggerClassName="w-full"
                    contentClassName="z-[100]"
                    label={tGlobal('defaultCountry')}
                    error={field.state.meta?.errors}
                  />
                );
              }}
            />

            <form.Field
              name="language"
              validators={{
                onChange: function (data) {
                  return parseFirstZodError(z.string().min(1, 'LANGUAGE_REQUIRED'), data.value);
                },
              }}
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <SelectField
                    value={field.state.value}
                    placeholder={tGlobal('selectALanguage')}
                    onChange={field.handleChange}
                    options={getLanguages()}
                    size="md"
                    contentFit={true}
                    className="w-full"
                    triggerClassName="w-full"
                    contentClassName="z-[100]"
                    label={tGlobal('defaultLanguage')}
                    error={field.state.meta?.errors}
                  />
                );
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  const stepConfigs: StepConfig[] = [
    {
      title: t('howDidYouHear'),
      description: t('originDescription'),
      icon: <Target02 width={24} height={24} className="text-branding-accent" />,
      leftContent: (
        <img
          src="/onboarding_1.png"
          alt="Step 0"
          className="h-full w-full max-w-[750px] object-contain"
        />
      ),
      showSkip: true,
      nextButtonText: t('next'),
      content: getFormContent(0),
    },
    {
      title: t('setupYourBrand'),
      description: t('setupYourBrandDescription'),
      icon: <Announcement03 width={24} height={24} className="text-branding-accent" />,
      leftContent: (
        <img
          src="/onboarding_2.png"
          alt="Step 1"
          className="h-full w-full max-w-[750px] object-contain"
        />
      ),
      showSkip: false,
      nextButtonText: t('finish'),
      content: getFormContent(1),
    },
  ];

  const loadindConfigs: LoadingConfig[] = [
    {
      title: t('loading1Title'),
      description: (
        <Trans
          t={t as unknown as TFunctionNonStrict}
          i18nKey="loadingDescription"
          components={{ strong: <strong /> }}
        />
      ),
      card: {
        image: '/brand-voice.png',
        title: (
          <Trans
            t={t as unknown as TFunctionNonStrict}
            i18nKey="card1Title"
            components={{ span: <span className="text-branding-accent" /> }}
          />
        ),
      },
    },
    {
      title: t('loading2Title'),
      description: (
        <Trans
          t={t as unknown as TFunctionNonStrict}
          i18nKey="loadingDescription"
          components={{ strong: <strong /> }}
        />
      ),
      card: {
        image: '/seo-score.png',
        title: (
          <Trans
            t={t as unknown as TFunctionNonStrict}
            i18nKey="card2Title"
            components={{ span: <span className="text-branding-accent" /> }}
          />
        ),
      },
    },
    {
      title: t('loading3Title'),
      description: (
        <Trans
          t={t as unknown as TFunctionNonStrict}
          i18nKey="loadingDescription"
          components={{ strong: <strong /> }}
        />
      ),
      card: {
        image: '/serp.png',
        title: (
          <Trans
            t={t as unknown as TFunctionNonStrict}
            i18nKey="card3Title"
            components={{ span: <span className="text-branding-accent" /> }}
          />
        ),
      },
    },
    {
      title: t('loading4Title'),
      description: (
        <Trans
          t={t as unknown as TFunctionNonStrict}
          i18nKey="loadingDescription"
          components={{ strong: <strong /> }}
        />
      ),
      card: {
        image: '/broono.png',
        title: (
          <Trans
            t={t as unknown as TFunctionNonStrict}
            i18nKey="card4Title"
            components={{ span: <span className="text-branding-accent" /> }}
          />
        ),
      },
    },
  ];

  useEffect(function () {
    if (sessionStorage.getItem('onboarding_started') == null) {
      capturePostHogEvent(PostHogEvents.ONBOARDING_STARTED);
      sessionStorage.setItem('onboarding_started', 'true');
    }
  }, []);

  if (isLoading) {
    const currentLoadingStep = Math.max(0, Math.min(loadingStep, loadindConfigs.length - 1));
    return (
      <div className="m-auto flex h-full w-full flex-col items-center justify-center md:w-[480px] md:w-fit">
        <BlymLoading className="scale-110 transform" />
        <p className="font-display-3 mb-2 mt-6">{loadindConfigs[currentLoadingStep].title}</p>
        <p className="font-body-1-m text-dark-gray">
          {loadindConfigs[currentLoadingStep].description}
        </p>

        <Slider
          items={loadindConfigs.map((config) => config.card)}
          autoplay={true}
          autoplayTime={10000}
          className="mt-6 w-full"
        />
      </div>
    );
  }

  return (
    <>
      <OnboardingLayout
        currentStep={currentStep + 1}
        totalSteps={maxStepsOnboarding + 1}
        title={stepConfigs[currentStep].title}
        description={stepConfigs[currentStep].description}
        icon={stepConfigs[currentStep].icon}
        leftContent={stepConfigs[currentStep].leftContent}
        onBack={currentStep >= 1 ? handleBack : undefined}
        onNext={handleNext}
        onSkip={stepConfigs[currentStep].showSkip ? handleSkip : undefined}
        skipLoading={skipButtonLoading}
        showSkip={
          isFeatureAvailable(Feature.SKIPABLE_ONBOARDING) && stepConfigs[currentStep].showSkip
        }
        nextButtonText={stepConfigs[currentStep].nextButtonText}
      >
        {stepConfigs[currentStep].content}
      </OnboardingLayout>
      {modalGenerationError ? (
        <Dialog open={modalGenerationError} onOpenChange={setModalGenerationError}>
          <DialogContent hasCloseButton>
            <FeaturedIcon icon={<AlertCircle />} color="red" size="md" className="m-auto mb-6" />
            <DialogTitle align="center" className="mb-1">
              {t('titleModalGenerationError')}
            </DialogTitle>
            <DialogDescription align="center">
              {t('descriptionModalGenerationError')}
            </DialogDescription>
            <DialogFooter>
              <Button
                variant="outline"
                size="xl"
                className="w-full"
                onClick={function () {
                  setModalGenerationError(false);
                  setCurrentStep(0);
                  setLoadingStep(-1);
                  setIsLoading(false);
                }}
              >
                {tGlobal('goBack')}
              </Button>
              <Button
                variant="accent"
                size="xl"
                className="w-full"
                onClick={function () {
                  setModalGenerationError(false);
                  void form.handleSubmit();
                }}
              >
                {tGlobal('retry')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
